import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { WorkspaceTrackingActions } from '../Workspace/tracking';
import { Button, Icon, IconButton, toasterService } from '@hevoio/hevo-ui-toolkit';
import { HdDropDown } from '../../../components';
import SearchArea from '../../../list/components/search-area/SearchArea';
import { matchPattern } from '../../../core/utils/string';
import { useConfirmV2 } from '../../../modal/components/confirm-dialog-v2/ConfirmDialogV2Provider';
import useUserService from '../../../auth/hooks/useUserService';
import useHasPermission from '../../../access-control/hooks/useHasPermission';
import useTeamSettings from '../hooks/useTeamSettingsService';
import { RbacPermissions } from '../../../core/interfaces/user';
import { WorkspacesAPI } from '../Workspace/api/WorkspacesAPI';
import { IWorkspace, IWorkspaceMember } from '../Workspace/interfaces/workspace';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import { Role } from '../models';
import { DropdownHelperAdornment } from './rbacHelpers';
import { InviteWorkspaceUsersModal } from './InviteWorkspaceUsersModal';
import { optionsTooltipResolver } from './models';
import { useRolesResolver } from './useRolesResolver';
import styles from './styles.module.scss';

function MemberAvatar({ name, email, index }: { name: string; email: string; index: number }) {
  const initial = (name || email || '?')[0].toUpperCase();
  return (
    <div className={`${styles.userImgContainer} list-icon-bg--${index % 3}`}>
      <span>{initial}</span>
    </div>
  );
}

interface WorkspaceMembersTabProps {
  roles: Role[];
}

export function WorkspaceMembersTab({ roles }: WorkspaceMembersTabProps) {
  const [workspaces, setWorkspaces] = useState<IWorkspace[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);
  const [members, setMembers] = useState<IWorkspaceMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<IWorkspace | null>(null);
  const [savingMemberId, setSavingMemberId] = useState<number | null>(null);
  // Explicit (non-linked) role selection per member while their dropdown is
  // open, so linked roles auto-select/deselect live as the user toggles.
  const [editingSelections, setEditingSelections] = useState<Record<number, Role[]>>({});
  const pendingRolesRef = useRef<Map<number, { roles: Role[]; original: string[] }>>(new Map());
  const { confirmV2 } = useConfirmV2();
  const { user: currentUser } = useUserService();
  const { eventTrack } = useAnalyticsTracker();
  const { hasPermission, isRbacEnabled } = useHasPermission();
  const { isWorkspacesEnabled, isISVTeam } = useTeamSettings();
  const canInviteOrEdit = !isRbacEnabled || hasPermission(RbacPermissions.TEAM_EDIT);
  // Same role linking as the Team tab: roles whose permissions are fully covered
  // by the selected roles get auto-selected (resolved via nesting) and locked.
  const { checkForRolesAccessOnMember } = useRolesResolver(roles);

  const allPermissions = useMemo(() => {
    const lookup = {};
    roles.forEach(role => {
      role.permissions.forEach(permission => {
        lookup[permission.permission_name] = permission;
      });
    });
    return lookup;
  }, [roles]);

  const HelperDocumentAdornmentCallback = useCallback(
    ({ option }) => <DropdownHelperAdornment allPermissions={allPermissions} option={option} />,
    [allPermissions]
  );

  const fetchWorkspaces = useCallback(() => {
    setWorkspacesLoading(true);
    WorkspacesAPI.getAllWorkspaces()
      .then(res => {
        setWorkspaces(res.data);
        setSelectedWorkspace(prev =>
          prev
            ? res.data.find(w => w.workspace_id === prev.workspace_id) ?? res.data[0] ?? null
            : res.data[0] ?? null
        );
      })
      .catch(err =>
        toasterService.pop(
          'error',
          undefined,
          getErrorMessageFromObj(err) || 'Failed to load workspaces'
        )
      )
      .finally(() => setWorkspacesLoading(false));
  }, []);

  useEffect(() => {
    if (isWorkspacesEnabled && isISVTeam) {
      fetchWorkspaces();
    }
  }, []);

  // Fetch members whenever selected workspace ID changes.
  // The cancelled flag prevents out-of-order responses from overwriting state
  // when the user switches workspaces faster than the previous request resolves.
  useEffect(() => {
    if (!selectedWorkspace) return;
    let cancelled = false;
    setMembers([]);
    setMembersLoading(true);
    WorkspacesAPI.getWorkspaceMembers(selectedWorkspace.workspace_id)
      .then(res => {
        if (!cancelled) setMembers(res.data);
      })
      .catch(err => {
        if (!cancelled)
          toasterService.pop(
            'error',
            undefined,
            getErrorMessageFromObj(err) || 'Failed to load members'
          );
      })
      .finally(() => {
        if (!cancelled) setMembersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWorkspace?.workspace_id]);

  const filteredMembers = useMemo(() => {
    return members.filter(m => {
      const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ');
      return !searchTerm || matchPattern(fullName, searchTerm) || matchPattern(m.email, searchTerm);
    });
  }, [members, searchTerm]);

  const handleRoleSelect = (member: IWorkspaceMember, selectedRoles: Role[]) => {
    // Keep only explicitly assigned roles; linked (resolved-via-nesting) roles
    // are derived from these on render and must not be persisted.
    const explicitRoles = (selectedRoles ?? []).filter(role => !role?.isResolvedViaNesting);
    const existing = pendingRolesRef.current.get(member.id);
    pendingRolesRef.current.set(member.id, {
      roles: explicitRoles,
      original: existing?.original ?? [...(member.roles ?? [])]
    });
    setEditingSelections(prev => ({ ...prev, [member.id]: explicitRoles }));
  };

  const handleRoleDropdownClose = async (member: IWorkspaceMember) => {
    const pending = pendingRolesRef.current.get(member.id);
    setEditingSelections(prev => {
      if (!(member.id in prev)) return prev;
      const next = { ...prev };
      delete next[member.id];
      return next;
    });
    if (!pending) return;

    const roleNames = pending.roles.filter(r => !r.isResolvedViaNesting).map(r => r.roleName);
    const prevRoles = pending.original;
    pendingRolesRef.current.delete(member.id);

    const hasChanged =
      roleNames.length !== prevRoles.length || roleNames.some(r => !prevRoles.includes(r));
    if (!hasChanged) return;

    setMembers(prev => prev.map(m => (m.id === member.id ? { ...m, roles: roleNames } : m)));
    setSavingMemberId(member.id);
    try {
      const res = await WorkspacesAPI.updateWorkspaceMemberRoles(
        selectedWorkspace?.workspace_id,
        member.id,
        roleNames
      );
      toasterService.pop(
        'success',
        undefined,
        res.success_message || 'Member roles updated successfully'
      );
      eventTrack({
        action: WorkspaceTrackingActions.CHANGE_MEMBER_ROLE_SUCCESS,
        properties: {
          workspaceId: selectedWorkspace?.workspace_id,
          userId: member.id,
          userEmail: member.email,
          roles: roleNames
        }
      });
    } catch (err) {
      setMembers(prev => prev.map(m => (m.id === member.id ? { ...m, roles: prevRoles } : m)));
      toasterService.pop(
        'error',
        undefined,
        getErrorMessageFromObj(err) || 'Failed to update member roles'
      );
    } finally {
      setSavingMemberId(null);
    }
  };

  const handleRemove = async (member: IWorkspaceMember) => {
    const confirmed = await confirmV2({
      title: 'Are you sure you want to delete this member?',
      body: 'This member will be permanently deleted from your workspace. Do you wish to continue?',
      positiveButtonText: 'Yes, Delete',
      negativeButtonText: 'Cancel',
      iconName: 'delete',
      iconContainerClass: 'error',
      positiveButtonClass: 'btn-error',
      positiveButtonVariation: 'faded'
    });
    if (!confirmed) return;
    try {
      const res = await WorkspacesAPI.removeWorkspaceMember(
        selectedWorkspace?.workspace_id,
        member.id
      );
      setMembers(prev => prev.filter(m => m.id !== member.id));
      toasterService.pop(
        'success',
        undefined,
        res.success_message || 'Member removed successfully'
      );
      eventTrack({
        action: WorkspaceTrackingActions.REMOVE_MEMBER_SUCCESS,
        properties: {
          workspaceId: selectedWorkspace?.workspace_id,
          userId: member.id,
          userEmail: member.email
        }
      });
    } catch (err) {
      toasterService.pop(
        'error',
        undefined,
        getErrorMessageFromObj(err) || 'Failed to remove member'
      );
    }
  };

  const loading = workspacesLoading || membersLoading;

  return (
    <>
      <div className={`box__header box__header--progress-bar ${styles.boxHeader}`}>
        <span className='box__title'>
          <div className={styles.titleGroup}>
            <span className={styles.titleText}>Users</span>
            <span className={styles.subtitleText}>
              Invite and manage who can access this workspace.
            </span>
          </div>
        </span>

        <div className={styles.wsHeaderActions}>
          <HdDropDown
            id='ws-workspace-filter'
            dataId='ws-workspace-filter'
            className={styles.wsFilterDropdown}
            options={workspaces}
            selected={selectedWorkspace}
            displayAccessor='name'
            valueAccessor='workspace_id'
            placeholder='Select Workspace'
            onChangeEventHandler={(selected: IWorkspace) => setSelectedWorkspace(selected)}
            size='small'
            selectMode
            hideClearable
            paperClasses={styles.dropdownWhitePaper}
          />
          {canInviteOrEdit && (
            <Button
              onClick={() => {
                eventTrack({
                  action: WorkspaceTrackingActions.INVITE_USERS_CLICK,
                  properties: { workspaceId: selectedWorkspace?.workspace_id }
                });
                setIsInviteOpen(true);
              }}
              dataId='invite-workspace-user-btn'
              icon='plus'
            >
              Invite User
            </Button>
          )}
        </div>
      </div>

      {!!members.length && (
        <div className={styles.inviteSection}>
          <div className={styles.label}>
            <span>{filteredMembers.length} User(s) found</span>
          </div>

          <div className={styles.inviteInputContainer}>
            <SearchArea
              dataId='ws-members-search'
              collapsible
              defaultExpanded={false}
              onSearch={setSearchTerm}
              placeholder='Search Members'
            />
          </div>
        </div>
      )}

      <div className='box__body'>
        {loading && !members.length ? (
          <div className={styles.loading}>Loading workspace members...</div>
        ) : null}

        {!loading && filteredMembers.length === 0 ? (
          <div className='no-item-box my-9'>
            <div className='no-item-box-icon-container'>
              <Icon name='team' className='no-item-box-icon' />
            </div>
            <span className='no-item-box-title'>No Members Found</span>
            <span className='no-item-box-desc'>No Members found for above search criteria.</span>
          </div>
        ) : null}

        {!loading &&
          filteredMembers.map((member, index) => {
            const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');
            const currentRoleObjs = roles.filter(r => (member.roles ?? []).includes(r.roleName));
            const explicitRoles = editingSelections[member.id] ?? currentRoleObjs;
            const { allSelectedRoles, disabledRoles, rolesActionsMap } =
              checkForRolesAccessOnMember(explicitRoles, RbacPermissions.TEAM_EDIT);
            const isSaving = savingMemberId === member.id;
            return (
              <div key={member.id} className={`${styles.member} alternate-table-row`}>
                <div className={styles.memberNameWrapper}>
                  <MemberAvatar name={fullName} email={member.email} index={index} />
                  <div className={styles.nameInfo}>
                    <div className={styles.nameRow}>
                      <span className='text-medium'>
                        {fullName || member.email}
                        {member.email === currentUser?.email && (
                          <span className={styles.youLabel}> (You)</span>
                        )}
                      </span>
                    </div>
                    {fullName && <span className={styles.emailText}>{member.email}</span>}
                  </div>
                </div>

                <div className={styles.memberActions}>
                  <div className={styles.memberDropdown}>
                    <HdDropDown
                      id={`ws-member-role-${index}`}
                      onChangeEventHandler={(selected: Role[]) =>
                        handleRoleSelect(member, selected)
                      }
                      onClose={() => handleRoleDropdownClose(member)}
                      selected={allSelectedRoles}
                      displayAccessor='title'
                      valueAccessor='roleName'
                      multiple
                      group
                      options={roles}
                      dataId={`ws-role-dropdown-${member.id}`}
                      disabled={isSaving || !canInviteOrEdit}
                      size='small'
                      selectMode
                      placeholder='Select Role'
                      disabledOptions={disabledRoles}
                      OptionTooltipComponent={option =>
                        optionsTooltipResolver(option, rolesActionsMap)
                      }
                      HelperDocumentAdornment={HelperDocumentAdornmentCallback}
                      helperAdornmentDirection='left'
                      popperContentClassName={styles.popperContentClassName}
                    />
                  </div>

                  {canInviteOrEdit && (
                    <IconButton
                      className={styles.dropdownTrigger}
                      onClick={() => handleRemove(member)}
                      dataId={`remove-ws-member-${member.id}`}
                      disabled={member.email === currentUser?.email}
                      label='Remove member'
                    >
                      <Icon name='delete' size={2} />
                    </IconButton>
                  )}
                </div>
              </div>
            );
          })}
      </div>

      <InviteWorkspaceUsersModal
        open={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onInviteSuccess={(emailCount?: number) => {
          eventTrack({
            action: WorkspaceTrackingActions.INVITE_USERS_SUCCESS,
            properties: { workspaceId: selectedWorkspace?.workspace_id, count: emailCount }
          });
          if (selectedWorkspace) {
            WorkspacesAPI.getWorkspaceMembers(selectedWorkspace.workspace_id)
              .then(res => setMembers(res.data))
              .catch(err =>
                toasterService.pop(
                  'error',
                  undefined,
                  getErrorMessageFromObj(err) || 'Failed to refresh members'
                )
              );
          }
        }}
        workspaces={workspaces}
        roles={roles}
      />
    </>
  );
}
